'use client';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

import { cn } from '~/utils';

interface DialogProps extends React.ComponentProps<typeof DialogPrimitive.Root> {}

export const Dialog: React.FC<DialogProps> = ({ ...props }) => {
    return <DialogPrimitive.Root data-slot="dialog" {...props} />;
};

interface DialogTriggerProps extends React.ComponentProps<typeof DialogPrimitive.Trigger> {}

export const DialogTrigger: React.FC<DialogTriggerProps> = ({ ...props }) => {
    return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
};

interface DialogPortalProps extends React.ComponentProps<typeof DialogPrimitive.Portal> {}

export const DialogPortal: React.FC<DialogPortalProps> = ({ ...props }) => {
    return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
};

interface DialogCloseProps extends React.ComponentProps<typeof DialogPrimitive.Close> {}

export const DialogClose: React.FC<DialogCloseProps> = ({ ...props }) => {
    return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
};

interface DialogOverlayProps extends React.ComponentProps<typeof DialogPrimitive.Overlay> {}

export const DialogOverlay: React.FC<DialogOverlayProps> = ({ className, ...props }) => {
    return (
        <DialogPrimitive.Overlay
            data-slot="dialog-overlay"
            className={cn(
                'fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
                className,
            )}
            {...props}
        />
    );
};

interface DialogContentProps extends React.ComponentProps<typeof DialogPrimitive.Content> {}

export const DialogContent: React.FC<DialogContentProps> = ({ className, children, ...props }) => {
    return (
        <DialogPortal>
            <DialogOverlay />
            <DialogPrimitive.Content
                data-slot="dialog-content"
                className={cn(
                    'fixed left-[50%] top-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 sm:max-w-lg',
                    className,
                )}
                {...props}
            >
                {children}
                <DialogPrimitive.Close className="absolute top-4 right-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-none disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-accent-foreground [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0">
                    <X />
                    <span className="sr-only">Close</span>
                </DialogPrimitive.Close>
            </DialogPrimitive.Content>
        </DialogPortal>
    );
};

interface DialogHeaderProps extends React.ComponentProps<'div'> {}

export const DialogHeader: React.FC<DialogHeaderProps> = ({ className, ...props }) => {
    return (
        <div
            data-slot="dialog-header"
            className={cn('flex flex-col space-y-1.5 text-center sm:text-left', className)}
            {...props}
        />
    );
};

interface DialogFooterProps extends React.ComponentProps<'div'> {}

export const DialogFooter: React.FC<DialogFooterProps> = ({ className, ...props }) => {
    return (
        <div
            data-slot="dialog-footer"
            className={cn(
                'flex flex-col-reverse space-y-2 sm:flex-row sm:justify-end sm:space-x-2 sm:space-y-0',
                className,
            )}
            {...props}
        />
    );
};

interface DialogTitleProps extends React.ComponentProps<typeof DialogPrimitive.Title> {}

export const DialogTitle: React.FC<DialogTitleProps> = ({ className, ...props }) => {
    return (
        <DialogPrimitive.Title
            data-slot="dialog-title"
            className={cn('text-lg font-semibold leading-none tracking-tight', className)}
            {...props}
        />
    );
};

interface DialogDescriptionProps extends React.ComponentProps<typeof DialogPrimitive.Description> {}

export const DialogDescription: React.FC<DialogDescriptionProps> = ({ className, ...props }) => {
    return (
        <DialogPrimitive.Description
            data-slot="dialog-description"
            className={cn('text-sm text-muted-foreground', className)}
            {...props}
        />
    );
};
